import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, TrendingDown, Play, Pause, Settings } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useOrderContext } from '../../../contexts/OrderContext';
import { usePositionContext } from '../../../contexts/PositionContext';
import { useAccount } from '../../../contexts/AccountContext';
import { useAccountType } from '../../../contexts/AccountTypeContext';
import { useAITrading } from '../../../contexts/AITradingContext';

interface SuperTrendStrategyProps {
  symbol: string;
  currentPrice?: string;
  onSignalUpdate?: (signal: 'buy' | 'sell' | null) => void;
  onConfigUpdate?: (config: any) => void;
  isRunning?: boolean;
}

interface SuperTrendConfig {
  period: number;
  multiplier: number;
  isActive: boolean;
  tradeAmountPercent: number;
}

const SuperTrendStrategy: React.FC<SuperTrendStrategyProps> = ({ symbol, currentPrice, onSignalUpdate, onConfigUpdate, isRunning: externalIsRunning }) => {
  const { theme } = useTheme();
  const { setOrderDetails, setShowOrderConfirmation, showConfirmationPopup } = useOrderContext();
  const { addOpenPosition } = usePositionContext();
  const { accountBalance } = useAccount();
  const { accountType } = useAccountType();
  const { executeAITrade, updateSignal } = useAITrading();
  
  const [config, setConfig] = useState<SuperTrendConfig>({
    period: 3,        // ATR period
    multiplier: 1.0,  // SuperTrend factor
    isActive: false,
    tradeAmountPercent: 10 // Default 10% of balance
  });
  
  const [currentSignal, setCurrentSignal] = useState<'buy' | 'sell' | null>(null);
  const [lastSignal, setLastSignal] = useState<'buy' | 'sell' | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  // Use external isRunning if provided, otherwise use internal state
  const actualIsRunning = externalIsRunning !== undefined ? externalIsRunning : isRunning;

  // Component initialization
  
  // Handle isRunning prop changes

  // Notify parent component when config changes
  useEffect(() => {
    onConfigUpdate?.(config);
  }, [config, onConfigUpdate]);

  // State for SuperTrend calculation
  const [priceHistory, setPriceHistory] = useState<Array<{price: number, timestamp: number}>>([]);
  const [lastDirection, setLastDirection] = useState<number | null>(null);
  const [lastSignalTime, setLastSignalTime] = useState<number>(0);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  // Calculate ATR (Average True Range)
  const calculateATR = useCallback((prices: Array<{price: number, timestamp: number}>, period: number): number => {
    if (prices.length < period + 1) return 0;
    
    let trueRanges: number[] = [];
    for (let i = 1; i < prices.length; i++) {
      const high = prices[i].price;
      const low = prices[i].price;
      const prevClose = prices[i - 1].price;
      
      const tr = Math.max(
        high - low,
        Math.abs(high - prevClose),
        Math.abs(low - prevClose)
      );
      trueRanges.push(tr);
    }
    
    if (trueRanges.length < period) return 0;
    
    // Calculate ATR as simple moving average of true ranges
    const recentTR = trueRanges.slice(-period);
    return recentTR.reduce((sum, tr) => sum + tr, 0) / period;
  }, []);

  // Calculate SuperTrend indicator
  const calculateSuperTrend = useCallback((prices: Array<{price: number, timestamp: number}>, atr: number, factor: number) => {
    if (prices.length < 2) return { superTrend: 0, direction: 0 };
    
    const currentPrice = prices[prices.length - 1].price;
    const prevPrice = prices[prices.length - 2].price;
    
    // Calculate basic upper and lower bands
    const upperBand = currentPrice + (factor * atr);
    const lowerBand = currentPrice - (factor * atr);
    
    // For simplicity, we'll use a basic trend direction based on price vs bands
    // In a full implementation, you'd track the previous SuperTrend value
    let direction = 1; // 1 for bullish, -1 for bearish
    
    if (currentPrice < lowerBand) {
      direction = -1; // Bearish
    } else if (currentPrice > upperBand) {
      direction = 1; // Bullish
    } else {
      // Use previous direction if price is between bands
      direction = lastDirection || 1;
    }
    
    const superTrend = direction === 1 ? lowerBand : upperBand;
    
    return { superTrend, direction };
  }, [lastDirection]);

  // Proper SuperTrend calculation with real market data
  const calculateSuperTrendSignal = useCallback(() => {
    if (!currentPrice || !actualIsRunning || isProcessing) {
      return;
    }
    
    const price = parseFloat(currentPrice);
    const now = Date.now();
    
    // Check minimum timeframe (1 minute = 60000ms)
    const minTimeframe = 60000; // 1 minute
    if (now - lastSignalTime < minTimeframe) {
      return;
    }
    
    // Set processing flag to prevent concurrent executions
    setIsProcessing(true);
    
    // Add current price to history
    const newPriceHistory = [...priceHistory, { price, timestamp: now }];
    
    // Keep only last 50 prices for calculation
    const limitedHistory = newPriceHistory.slice(-50);
    setPriceHistory(limitedHistory);
    
    // Need at least 5 prices for SuperTrend calculation
    if (limitedHistory.length < 5) {
      return;
    }
    
    // Calculate ATR
    const atr = calculateATR(limitedHistory, config.period);
    
    if (atr === 0) {
      return;
    }
    
    // Calculate SuperTrend
    const { superTrend, direction } = calculateSuperTrend(limitedHistory, atr, config.multiplier);
    
    // Check for direction change (this is the key signal)
    let signal: 'buy' | 'sell' | null = null;
    
    if (lastDirection !== null && lastDirection !== direction) {
      // Direction changed - this is our signal
      if (lastDirection === -1 && direction === 1) {
        // Changed from bearish to bullish - BUY signal
        signal = 'buy';
      } else if (lastDirection === 1 && direction === -1) {
        // Changed from bullish to bearish - SELL signal
        signal = 'sell';
      }
    }
    
    // Update direction for next calculation
    setLastDirection(direction);
    
    if (signal) {
      setCurrentSignal(signal);
      setLastSignal(signal);
      setLastSignalTime(now);
      onSignalUpdate?.(signal);
      updateSignal(symbol, signal);
      
      // Execute AI trade when signal changes
      executeAITrade(symbol, signal, currentPrice);
    }
    
    // Clear processing flag
    setIsProcessing(false);
  }, [
    currentPrice, 
    actualIsRunning, 
    isProcessing,
    priceHistory, 
    lastDirection, 
    lastSignalTime, 
    config.period, 
    config.multiplier, 
    onSignalUpdate, 
    symbol, 
    updateSignal, 
    executeAITrade, 
    calculateATR, 
    calculateSuperTrend
  ]);


  // Calculate SuperTrend periodically
  useEffect(() => {
    if (!actualIsRunning) {
      return;
    }
    
    // Check every 5 seconds for price updates, but only generate signals based on SuperTrend logic
    const intervalTime = 5000; // 5 seconds
    const interval = setInterval(calculateSuperTrendSignal, intervalTime);
    return () => {
      clearInterval(interval);
    };
  }, [calculateSuperTrendSignal, actualIsRunning, symbol]);

  const handleStartStop = () => {
    setIsRunning(!isRunning);
    if (!isRunning) {
      // Reset all state when starting
      setCurrentSignal(null);
      setLastSignal(null);
      setPriceHistory([]);
      setLastDirection(null);
      setLastSignalTime(0);
      setIsProcessing(false);
    }
  };

  const handleConfigChange = (field: keyof SuperTrendConfig, value: number | boolean) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-4">
      {/* Strategy Configuration */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs theme-text-quaternary">Period:</span>
          <input
            type="number"
            value={config.period}
            onChange={(e) => handleConfigChange('period', parseInt(e.target.value))}
            className="w-16 px-2 py-1 text-xs bg-transparent border theme-border rounded text-right theme-text-primary"
            min="1"
            max="50"
          />
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-xs theme-text-quaternary">Multiplier:</span>
          <input
            type="number"
            value={config.multiplier}
            onChange={(e) => handleConfigChange('multiplier', parseFloat(e.target.value))}
            className="w-16 px-2 py-1 text-xs bg-transparent border theme-border rounded text-right theme-text-primary"
            min="0.1"
            max="10"
            step="0.1"
          />
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-xs theme-text-quaternary">Trade Amount:</span>
          <div className="flex items-center space-x-1">
            <input
              type="number"
              value={config.tradeAmountPercent}
              onChange={(e) => handleConfigChange('tradeAmountPercent', parseFloat(e.target.value))}
              className="w-12 px-2 py-1 text-xs bg-transparent border theme-border rounded text-right theme-text-primary"
              min="1"
              max="100"
              step="1"
            />
            <span className="text-xs theme-text-quaternary">%</span>
          </div>
        </div>
        
        {/* Trade Amount Display */}
        {accountBalance && (
          <div className="flex items-center justify-between pt-2 border-t theme-border">
            <span className="text-xs theme-text-quaternary">Trade Value:</span>
            <span className="text-xs font-medium theme-text-primary">
              ${((accountType === 'demo' ? accountBalance.demoBalance : accountBalance.liveBalance) * config.tradeAmountPercent / 100).toFixed(2)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperTrendStrategy;
